<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const movementTypes = [
  {
    type: 'Pembelian',
    icon: 'ri-shopping-bag-line',
    color: 'success',
    direction: '+ Tambah',
    example: 'Anda terima 5 kg Kopi Arabika dari supplier → stok kopi bertambah 5.000 gram',
  },
  {
    type: 'Penjualan',
    icon: 'ri-shopping-cart-2-line',
    color: 'error',
    direction: '− Kurang',
    example: 'Kasir jual 1 Americano (butuh 20g kopi) → stok kopi berkurang 20 gram',
  },
  {
    type: 'Stock Adjustment',
    icon: 'ri-equalizer-line',
    color: 'warning',
    direction: '+ / −',
    example: 'Anda temukan stok susu tumpah, koreksi stok dari 2.000ml jadi 1.700ml',
  },
  {
    type: 'Stock Opname',
    icon: 'ri-clipboard-line',
    color: 'info',
    direction: '+ / −',
    example: 'Hasil hitung fisik: stok gula berbeda 500g dari data sistem → sistem sesuaikan',
  },
  {
    type: 'Void Penjualan',
    icon: 'ri-arrow-go-back-line',
    color: 'secondary',
    direction: '+ Tambah',
    example: 'Transaksi dibatalkan → bahan baku yang sudah dikurangi dikembalikan ke stok',
  },
  {
    type: 'Stok Awal',
    icon: 'ri-database-2-line',
    color: 'primary',
    direction: '+ Tambah',
    example: 'Pertama kali input bahan baku "Gula Pasir" dengan stok awal 10.000 gram',
  },
]

const faq = [
  {
    q: 'Apakah saya bisa menghapus atau mengubah catatan pergerakan stok?',
    a: 'Tidak. Riwayat pergerakan stok bersifat permanen dan tidak bisa diubah atau dihapus. Ini disengaja untuk menjaga akurasi laporan dan audit trail bisnis Anda. Jika ada kesalahan stok, gunakan fitur Stock Adjustment untuk koreksi — koreksi tersebut akan tercatat sebagai entri baru, bukan mengubah yang lama.',
  },
  {
    q: 'Kenapa stok berkurang tapi saya tidak jual apa-apa?',
    a: 'Kemungkinan penyebabnya: (1) Ada transaksi penjualan yang belum Anda ketahui — cek kolom "Dibuat Oleh" untuk melihat siapa yang melakukan transaksi. (2) Ada koreksi stok (Stock Adjustment) yang dilakukan. (3) Ada konfirmasi Stock Opname. Gunakan filter Tipe untuk mempersempit pencarian.',
  },
  {
    q: 'Data pergerakan stok bisa dilihat dari berapa lama ke belakang?',
    a: 'Semua riwayat sejak toko Anda pertama kali beroperasi tersimpan. Anda bisa filter berdasarkan rentang tanggal untuk melihat periode tertentu, misalnya hanya bulan ini atau 3 bulan terakhir.',
  },
  {
    q: 'Kolom "Stok Sebelum" dan "Stok Sesudah" menunjukkan apa?',
    a: '"Stok Sebelum" adalah jumlah stok tepat sebelum transaksi terjadi. "Stok Sesudah" adalah jumlah stok setelah transaksi selesai diproses. Selisihnya adalah kolom "Qty". Ini memudahkan Anda melacak perubahan stok satu per satu.',
  },
  {
    q: 'Apakah saya bisa ekspor data ini ke Excel?',
    a: 'Fitur ekspor ke Excel tersedia di menu Laporan → Laporan Inventory. Di halaman Pergerakan Stok ini, Anda hanya bisa melihat data secara langsung di layar.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink
            to="/userguide/produk-inventory"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            Produk &amp; Inventory
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Pergerakan Stok</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="secondary" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-history-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Riwayat Pergerakan Stok</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Pantau setiap perubahan stok bahan baku secara real-time
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda memahami dan menggunakan halaman Riwayat Pergerakan Stok —
          buku besar digital yang mencatat setiap keluar-masuk bahan baku di toko Anda.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="secondary" />
            Apa itu Pergerakan Stok?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Bayangkan Anda punya buku kas keuangan — setiap uang masuk dan keluar dicatat
              lengkap dengan tanggal, jumlah, dan keterangannya.
              <strong>Pergerakan Stok</strong> adalah versi digitalnya, tapi untuk bahan baku.
            </p>

            <p class="text-body-2 mb-3">
              Setiap kali ada aktivitas yang mengubah jumlah stok — entah itu penjualan,
              pembelian, atau koreksi manual — sistem otomatis mencatat satu baris baru
              di halaman ini. Anda tidak perlu input manual sama sekali.
            </p>

            <VAlert type="success" variant="tonal" class="mb-3">
              <strong>Kenapa ini penting?</strong>
              Dengan riwayat yang lengkap, Anda bisa menjawab pertanyaan seperti:
              "Kenapa stok kopi tiba-tiba berkurang 2 kg tadi malam?" atau
              "Siapa yang melakukan koreksi stok kemarin?"
            </VAlert>

            <VAlert type="warning" variant="tonal" density="compact">
              <strong>Hanya bisa dilihat, tidak bisa diubah.</strong>
              Halaman ini bersifat <em>read-only</em> (hanya tampilan). Riwayat yang sudah
              tercatat tidak bisa dihapus atau diedit — ini untuk menjaga keakuratan data Anda.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Jenis Pergerakan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-exchange-line" class="me-2" color="primary" />
            6 Jenis Pergerakan Stok
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Ada 6 penyebab stok berubah. Setiap baris di tabel menunjukkan salah satu dari ini:
            </p>

            <VRow>
              <VCol
                v-for="item in movementTypes"
                :key="item.type"
                cols="12"
                md="6"
              >
                <VCard variant="outlined" class="h-100">
                  <VCardText class="pa-3">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VAvatar :color="item.color" variant="tonal" size="36" rounded>
                        <VIcon :icon="item.icon" size="18" />
                      </VAvatar>
                      <div>
                        <p class="text-body-2 font-weight-bold mb-0">{{ item.type }}</p>
                        <VChip :color="item.color" size="x-small" variant="tonal">
                          {{ item.direction }}
                        </VChip>
                      </div>
                    </div>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ item.example }}
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Pakai -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-eye-line" class="me-2" color="info" />
            Cara Membaca Halaman Pergerakan Stok
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-5">
              <li class="mb-3">
                Buka menu <strong>Inventory → Pergerakan Stok</strong> di sidebar kiri
              </li>
              <li class="mb-3">
                Tabel akan tampil dengan data terbaru di bagian paling atas.
                Setiap baris adalah <strong>satu kejadian perubahan stok</strong>.
              </li>
              <li class="mb-3">
                Gunakan <strong>filter</strong> untuk mempersempit data yang ditampilkan:
                <VTable class="mt-2 mb-2">
                  <thead>
                    <tr>
                      <th style="min-width: 140px">Filter</th>
                      <th>Fungsi &amp; Contoh Penggunaan</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Tipe Movement</strong></td>
                      <td class="text-body-2 py-2">
                        Tampilkan hanya perubahan akibat penjualan, pembelian, atau adjustment.
                        <br>Contoh: pilih "Penjualan" untuk lihat berapa bahan yang terpakai hari ini.
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Dari Tanggal</strong></td>
                      <td class="text-body-2 py-2">
                        Batas awal rentang tanggal yang ingin ditampilkan.
                        <br>Contoh: "01/06/2025" untuk lihat data mulai awal Juni.
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Sampai Tanggal</strong></td>
                      <td class="text-body-2 py-2">
                        Batas akhir rentang tanggal.
                        <br>Contoh: "30/06/2025" untuk data sampai akhir Juni.
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>
              <li class="mb-3">
                Klik <strong>"Refresh"</strong> jika Anda baru saja melakukan transaksi
                dan ingin memastikan data sudah terupdate.
              </li>
            </ol>

            <!-- Penjelasan kolom tabel -->
            <p class="text-body-2 font-weight-bold mb-3">📋 Penjelasan Kolom Tabel:</p>
            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 140px">Kolom</th>
                  <th>Arti &amp; Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Tanggal</strong></td>
                  <td class="text-body-2 py-2">
                    Waktu persis kejadian perubahan stok.
                    <br>Contoh: <em>15 Jun 2025, 09:32</em>
                  </td>
                </tr>
                <tr>
                  <td><strong>Bahan Baku</strong></td>
                  <td class="text-body-2 py-2">
                    Nama bahan baku yang stoknya berubah, beserta satuannya.
                    <br>Contoh: <em>Kopi Arabika (gram)</em>
                  </td>
                </tr>
                <tr>
                  <td><strong>Tipe</strong></td>
                  <td class="text-body-2 py-2">
                    Label warna yang menunjukkan penyebab perubahan stok.
                    <br>Contoh: badge hijau "Pembelian", badge merah "Penjualan"
                  </td>
                </tr>
                <tr>
                  <td><strong>Qty</strong></td>
                  <td class="text-body-2 py-2">
                    Jumlah perubahan. Angka <span class="text-success font-weight-bold">hijau (+)</span> berarti stok bertambah,
                    angka <span class="text-error font-weight-bold">merah (−)</span> berarti stok berkurang.
                    <br>Contoh: <em class="text-error">−20</em> (berkurang 20 gram karena penjualan)
                  </td>
                </tr>
                <tr>
                  <td><strong>Stok Sebelum</strong></td>
                  <td class="text-body-2 py-2">
                    Jumlah stok sebelum kejadian ini terjadi.
                    <br>Contoh: <em>2.500</em> (gram)
                  </td>
                </tr>
                <tr>
                  <td><strong>Stok Sesudah</strong></td>
                  <td class="text-body-2 py-2">
                    Jumlah stok setelah kejadian ini selesai diproses.
                    <br>Contoh: <em>2.480</em> (2.500 − 20 = 2.480 gram)
                  </td>
                </tr>
                <tr>
                  <td><strong>Dibuat Oleh</strong></td>
                  <td class="text-body-2 py-2">
                    Nama akun pengguna yang memicu perubahan.
                    <br>Contoh: <em>Budi (Kasir)</em> atau <em>Admin</em>
                  </td>
                </tr>
                <tr>
                  <td><strong>Keterangan</strong></td>
                  <td class="text-body-2 py-2">
                    Catatan tambahan yang menjelaskan konteks perubahan.
                    <br>Contoh: <em>"Stok tumpah saat pindah gudang"</em>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="info" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Cek pergerakan stok setiap akhir hari.</strong>
                  Bandingkan jumlah penjualan di kasir dengan jumlah bahan yang berkurang.
                  Jika selisihnya tidak masuk akal, mungkin ada bahan yang terbuang
                  atau dicatat dengan recipe yang salah.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Gunakan filter "Adjustment" untuk audit.</strong>
                  Jika ada koreksi stok yang tidak Anda ketahui, filter tipe "Adjustment"
                  dan cek kolom "Dibuat Oleh" — Anda bisa tahu siapa yang melakukan koreksi.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Lihat "Stok Sesudah" sebagai cek cepat.</strong>
                  Kolom ini menunjukkan stok aktual setelah transaksi. Bandingkan dengan
                  stok yang terlihat di halaman Bahan Baku — seharusnya sama persis.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Halaman Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Bahan Baku', sub: 'Lihat stok saat ini semua bahan', icon: 'ri-archive-line', color: 'info', to: '/userguide/produk-inventory/bahan-baku' },
              { title: 'Stock Adjustment', sub: 'Koreksi stok jika ada selisih', icon: 'ri-equalizer-line', color: 'warning', to: '/userguide/produk-inventory/stock-adjustment' },
              { title: 'Stock Opname', sub: 'Hitung fisik dan rekonsiliasi stok', icon: 'ri-clipboard-line', color: 'success', to: '/userguide/produk-inventory/stock-opname' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
